<template>
  <div
    :id="message.id"
    :class="cssClass"
    ref="message"
    @mouseenter="isHovered = true"
    @mouseleave="onMouseLeave"
  >
    <div v-if="isTarget" class="p-message__hr">未読メッセージ</div>

    <div class="p-message__inner">
      <div class="p-message__body">
        <div class="p-message__main">
          <div
            v-if="hasActions"
            :class="[actionsClass, { 'is-visible': shouldShowDesktopActions }]"
            class="p-message__actions"
          >
            <button
              ref="actionsButton"
              @click.stop="toggleMenu"
              class="p-message__actions-btn"
            >
              <img
                src="/assets/icons/ThreeDots.svg"
                alt=""
                class="p-message__actions-icon"
              />
            </button>
            <ul
              v-if="isMenuOpen"
              ref="dropdown"
              :style="dropdownStyle"
              class="p-message__dropdown"
            >
              <li
                v-if="canMarkUnread"
                @click="markUnread"
                class="p-message__dropdown-item"
              >
                <img
                  src="/assets/icons/Mail.svg"
                  alt=""
                  class="p-message__dropdown-icon"
                />
                <span>未読に戻す</span>
              </li>
              <li
                v-if="canEditMessage"
                @click="startEdit"
                class="p-message__dropdown-item"
              >
                <img
                  src="/assets/icons/Pencil.svg"
                  alt=""
                  class="p-message__dropdown-icon"
                />
                <span>編集する</span>
              </li>
              <li
                v-if="canEditMessage"
                @click="confirmDelete"
                class="p-message__dropdown-item is-danger"
              >
                <img
                  src="/assets/icons/Dustbox.svg"
                  alt=""
                  class="p-message__dropdown-icon"
                />
                <span>削除</span>
              </li>
            </ul>
          </div>

          <div v-if="isEditing" class="p-message__edit">
            <textarea
              v-model="editText"
              class="p-message__edit-textarea"
              rows="3"
            ></textarea>
            <div class="p-message__edit-footer">
              <button @click="cancelEdit" class="p-message__edit-cancel">
                キャンセル
              </button>
              <button
                @click="saveEdit"
                :disabled="!editText.trim() || isSaving"
                class="p-message__edit-save"
              >
                保存する
              </button>
            </div>
          </div>

          <div v-else class="p-message__content" @click="handleContentClick">
            <p v-if="message.isDeleted" class="p-message__deleted-text">
              このメッセージは削除されました
            </p>
            <template v-else>
              <div
                v-if="message.thumbnail"
                class="p-message__preview"
                :class="{ 'is-disabled': uploadState !== 'ready' }"
                @click.stop="uploadState === 'ready' ? openModal() : null"
              >
                <div class="c-image--thumbnail">
                  <img :src="message.thumbnail" alt="" />
                  <div
                    v-if="uploadState !== 'ready'"
                    class="p-message__upload-overlay"
                  >
                    <div
                      v-if="uploadState === 'uploading'"
                      class="p-message__upload-spinner"
                    ></div>
                    <div
                      v-else
                      class="p-message__upload-error"
                      title="画像のアップロードに失敗しました"
                    >
                      <span class="p-message__upload-error-icon">!</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="message.fileUrl">
                <a
                  :href="message.fileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                >
                  {{ fileName }}
                </a>
              </div>
              <p v-if="message.message" v-html="messageHtml"></p>
            </template>
          </div>

          <div class="p-message__footer">
            <transition name="v-fade" mode="out-in">
              <Badge v-if="!isMine && isUnread" variant="dot" />
              <p v-if="isMine && !isUnreadPartner" class="p-message__status">
                既読
              </p>
            </transition>
            <p class="p-message__date">{{ date }}</p>
            <span
              v-if="message.isEdited && !message.isDeleted"
              class="p-message__edited-label"
            >
              編集済み
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="p-message__avatar">
      <Avatar :src="avatar" />
    </div>

    <Modal v-if="isOpenModal" appear size="lg" @close="closeModal">
      <template slot="title">
        {{ fileName }}
      </template>
      <template slot="content">
        <div class="c-text--center">
          <Loading v-if="isLoadingImage" :height="50" variant="inline" />
          <p v-else-if="error">{{ error }}</p>
          <a
            v-else
            :href="message.imageUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img :src="message.imageUrl" alt="" />
          </a>
        </div>
      </template>
    </Modal>

    <div
      v-if="isActionSheetOpen"
      class="p-message__sheet-overlay"
      @click="closeActionSheet"
    >
      <div class="p-message__sheet" @click.stop>
        <div class="p-message__sheet-header">
          <p class="p-message__sheet-title">メッセージを操作</p>
          <button class="p-message__sheet-close" @click="closeActionSheet">
            <img src="/assets/icons/Cross.svg" alt="" />
          </button>
        </div>
        <button
          v-if="canMarkUnread"
          class="p-message__sheet-item"
          @click="markUnread"
        >
          <img
            src="/assets/icons/Mail.svg"
            alt=""
            class="p-message__dropdown-icon"
          />
          <span>未読に戻す</span>
        </button>
        <button
          v-if="canEditMessage"
          class="p-message__sheet-item"
          @click="startEdit"
        >
          <img
            src="/assets/icons/Pencil.svg"
            alt=""
            class="p-message__dropdown-icon"
          />
          <span>編集する</span>
        </button>
        <button
          v-if="canEditMessage"
          class="p-message__sheet-item is-danger"
          @click="confirmDelete"
        >
          <img
            src="/assets/icons/Dustbox.svg"
            alt=""
            class="p-message__dropdown-icon"
          />
          <span>削除</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ERROR_MESSAGE } from '@/constants';
import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Loading from '@/components/Loading';
import Modal from '@/components/Modal';
import utils from '@/utils';

export default {
  props: {
    firstUnreadMessageId: {
      type: String,
      required: false
    },
    message: {
      type: Object,
      required: true
    },
    roomLastDatetime: {
      type: Object,
      required: true
    }
  },
  components: {
    Avatar,
    Badge,
    Loading,
    Modal
  },
  data() {
    return {
      elTimeline: null,
      error: '',
      timeoutId: null,
      uploadTimeoutId: null,
      isInview: false,
      isLoadingImage: true,
      isOpenModal: false,
      isUnread: false,
      isUploadTimedOut: false,
      isHovered: false,
      isMenuOpen: false,
      isActionSheetOpen: false,
      dropdownStyle: {},
      isDropdownBelow: false,
      isMobileViewport: false,
      isEditing: false,
      isSaving: false,
      editText: ''
    };
  },
  computed: {
    uploadState() {
      if (!this.message.thumbnail || this.message.imageUrl) {
        return 'ready';
      }
      if (this.isUploadTimedOut) {
        return 'failed';
      }
      return 'uploading';
    },
    avatar() {
      let avatar = '';
      if (this.isMine) {
        avatar = this.user.picture;
      } else if (this.room) {
        avatar = this.room.partnerAvatar;
      }
      return avatar;
    },
    cssClass() {
      return {
        'p-message': true,
        'p-message--left': !this.isMine,
        'p-message--right': this.isMine,
        'is-target': this.isTarget,
        'is-deleted': this.message.isDeleted
      };
    },
    date() {
      let date = '';
      if (this.message.datetime) {
        date = this.$options.filters.dateString(
          this.message.datetime.seconds * 1000,
          'M月D日 H:mm'
        );
      }
      return date;
    },
    fileName() {
      let name = '';
      if (this.message.storageUri) {
        name = this.$firebase.getImageData(this.message.storageUri).name;
      }
      return name;
    },
    room() {
      return this.$store.getters.currentRoom;
    },
    roomPartnerLastDatetime() {
      return this.room ? this.room.partnerLastDatetime : null;
    },
    user() {
      return this.$store.state.user;
    },
    isMine() {
      return this.message.uid === this.$store.getters.effectiveSub;
    },
    isCompany() {
      return utils.isCompany(this.$store.state.role.name);
    },
    isReadOnlyMode() {
      return this.$store.state.isReadOnlyMode;
    },
    canEditMessage() {
      if (this.isReadOnlyMode) {
        return false;
      }
      return this.isMine && !this.message.isDeleted;
    },
    canMarkUnread() {
      if (this.isReadOnlyMode) {
        return false;
      }
      return this.isCompany && !this.message.isDeleted;
    },
    hasActions() {
      return this.canEditMessage || this.canMarkUnread;
    },
    actionsClass() {
      return {
        'is-dropdown-below': this.isDropdownBelow
      };
    },
    shouldShowDesktopActions() {
      return (
        this.hasActions &&
        !this.isMobileViewport &&
        !this.isEditing &&
        (this.isHovered || this.isMenuOpen)
      );
    },
    isTarget() {
      return this.message.id === this.firstUnreadMessageId;
    },
    isUnreadPartner() {
      return (
        !this.message.datetime ||
        !this.roomPartnerLastDatetime ||
        utils.compareTimestamp(
          this.message.datetime,
          this.roomPartnerLastDatetime
        ) > 0
      );
    },
    messageHtml() {
      return utils.formatMessageHtml(this.message.message);
    }
  },
  watch: {
    'message.imageUrl'(newUrl) {
      if (newUrl) {
        clearTimeout(this.uploadTimeoutId);
        this.isUploadTimedOut = false;
      }
    }
  },
  created() {
    this.isUnread =
      !this.message.datetime ||
      !this.roomLastDatetime ||
      utils.compareTimestamp(this.message.datetime, this.roomLastDatetime) > 0;
  },
  mounted() {
    this.elTimeline = document.getElementById('timeline');
    this.updateViewportState();
    if (this.isUnread) {
      this.addScrollEvent();
      this.checkInview();
    }
    document.addEventListener('click', this.closeMenu);
    window.addEventListener('resize', this.updateViewportState);
    // Check image upload state for timeout handling
    if (!this.message.thumbnail || this.message.imageUrl) return;

    const isOldMessage =
      this.message.datetime &&
      Date.now() / 1000 - this.message.datetime.seconds > 60;
    if (isOldMessage) {
      this.isUploadTimedOut = true;
    } else {
      this.uploadTimeoutId = setTimeout(() => {
        this.isUploadTimedOut = true;
      }, 60000);
    }
  },
  beforeDestroy() {
    clearTimeout(this.timeoutId);
    clearTimeout(this.uploadTimeoutId);
    this.removeScrollEvent();
    document.removeEventListener('click', this.closeMenu);
    window.removeEventListener('resize', this.updateViewportState);
  },
  methods: {
    syncTimelineLayout() {
      this.$nextTick(() => {
        this.$root.$emit('room-form-layout-change', {
          messageId: this.message.id
        });
      });
    },
    addScrollEvent() {
      this.elTimeline.addEventListener('scroll', this.checkInview);
    },
    removeScrollEvent() {
      this.elTimeline.removeEventListener('scroll', this.checkInview);
    },
    checkInview() {
      if (!this.elTimeline || !this.$refs.message) {
        return;
      }
      const timelineRect = this.elTimeline.getBoundingClientRect();
      const messageRect = this.$refs.message.getBoundingClientRect();
      const messageMiddle =
        messageRect.top - timelineRect.top + messageRect.height / 2;
      this.isInview = messageMiddle > 0 && messageMiddle < timelineRect.height;
      if (this.isInview) {
        this.timeoutId = setTimeout(() => {
          this.isUnread = false;
        }, 1500);
        this.removeScrollEvent();
      }
    },
    openModal() {
      if (this.uploadState !== 'ready') return;
      this.isOpenModal = true;
      if (this.isLoadingImage) {
        utils
          .loadImage(this.message.imageUrl)
          .catch(() => {
            this.error = ERROR_MESSAGE.imageLoad;
          })
          .finally(() => {
            this.isLoadingImage = false;
          });
      }
    },
    closeModal() {
      this.isOpenModal = false;
    },
    updateViewportState() {
      this.isMobileViewport = window.innerWidth <= 767;
      if (this.isMobileViewport) {
        this.isMenuOpen = false;
      } else {
        this.isActionSheetOpen = false;
      }
    },
    handleContentClick(event) {
      const isLinkTarget =
        event.target && typeof event.target.closest === 'function'
          ? event.target.closest('a')
          : null;
      if (
        !this.isMobileViewport ||
        !this.hasActions ||
        this.message.isDeleted ||
        isLinkTarget
      ) {
        return;
      }
      this.isActionSheetOpen = true;
    },
    onMouseLeave() {
      this.isHovered = false;
      if (!this.isMenuOpen) {
        this.isDropdownBelow = false;
      }
    },
    toggleMenu() {
      if (this.isMobileViewport || !this.hasActions) {
        return;
      }
      this.isMenuOpen = !this.isMenuOpen;
      if (!this.isMenuOpen) {
        this.isDropdownBelow = false;
        return;
      }
      this.$nextTick(() => {
        this.updateDropdownPosition();
      });
    },
    closeMenu() {
      this.isMenuOpen = false;
      this.dropdownStyle = {};
      this.isDropdownBelow = false;
    },
    closeActionSheet() {
      this.isActionSheetOpen = false;
    },
    updateDropdownPosition() {
      if (!this.$refs.actionsButton || !this.$refs.dropdown) {
        return;
      }
      const triggerRect = this.$refs.actionsButton.getBoundingClientRect();
      const dropdownWidth = this.$refs.dropdown.offsetWidth;
      const dropdownHeight = this.$refs.dropdown.offsetHeight;
      const viewportPadding = 8;
      this.isDropdownBelow = triggerRect.top < dropdownHeight + 12;

      let left = this.isMine
        ? triggerRect.left - dropdownWidth - 8
        : triggerRect.right + 8;
      let top = this.isDropdownBelow
        ? triggerRect.bottom + 8
        : triggerRect.top + triggerRect.height / 2 - dropdownHeight / 2;

      left = Math.max(viewportPadding, left);
      left = Math.min(
        left,
        window.innerWidth - dropdownWidth - viewportPadding
      );
      top = Math.max(viewportPadding, top);
      top = Math.min(
        top,
        window.innerHeight - dropdownHeight - viewportPadding
      );

      this.dropdownStyle = {
        left: `${left}px`,
        top: `${top}px`
      };
    },
    markUnread() {
      if (!this.canMarkUnread || !this.message.datetime) {
        return;
      }
      const hasManualUnread = !!this.room.companyManualUnreadMessageId;
      const data = {
        company_manual_unread_datetime: this.message.datetime,
        company_manual_unread_message_id: this.message.id
      };
      if (!hasManualUnread) {
        data.company_total_unread = this.$firebase.increment(1);
      }
      this.isMenuOpen = false;
      this.closeActionSheet();
      this.$store.dispatch('setRoom', {
        companyManualUnreadDatetime: this.message.datetime,
        companyManualUnreadMessageId: this.message.id,
        id: this.room.id
      });
      this.$root.$emit('room-manual-unread-set', {
        datetime: this.message.datetime,
        messageId: this.message.id,
        roomId: this.room.id
      });
      this.$firebase.updateRoomData(this.room.id, data).catch(() => {});
    },
    startEdit() {
      if (this.isReadOnlyMode) {
        return;
      }
      if (this.isMobileViewport) {
        this.closeActionSheet();
        this.$root.$emit('room-form-edit-start', {
          id: this.message.id,
          message: this.message.message || ''
        });
        return;
      }
      this.editText = this.message.message || '';
      this.isEditing = true;
      this.isMenuOpen = false;
      this.syncTimelineLayout();
    },
    cancelEdit() {
      this.isEditing = false;
      this.editText = '';
      this.syncTimelineLayout();
    },
    saveEdit() {
      if (this.isReadOnlyMode) {
        return;
      }
      const text = this.editText.trim();
      if (!text || this.isSaving) {
        return;
      }
      this.isSaving = true;
      this.$firebase
        .updateMessageData(this.room.id, this.message.id, {
          isEdited: true,
          message: text
        })
        .then(() => {
          this.isEditing = false;
          this.editText = '';
          this.syncTimelineLayout();
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    confirmDelete() {
      if (this.isReadOnlyMode) {
        return;
      }
      this.isMenuOpen = false;
      this.closeActionSheet();
      this.$root.$emit('room-form-edit-clear', this.message.id);
      this.$firebase.updateMessageData(this.room.id, this.message.id, {
        isDeleted: true
      });
    }
  }
};
</script>
